import { Dialog, Transition } from '@headlessui/react';
import PropTypes from 'prop-types';
import { Fragment, useState } from 'react';
import { toast } from 'react-toastify';

import { useGetUserQuery } from '@/features/auth/auth.api';

interface MigrationDialogProps {
  isOpen: boolean;
  onClose: () => void;
}

const deadline = '7th September';
const newDashboardUrl = 'https://dashboard.nexlev.io?migration=true';

const MigrationDialog: React.FC<MigrationDialogProps> = ({
  isOpen,
  onClose,
}) => {
  const { data: user } = useGetUserQuery(null);
  const [copySuccess, setCopySuccess] = useState(false);

  const handleNewDashboard = () => {
    window.open(newDashboardUrl, '_blank');
  };

  const handleCopyMigrationCode = async () => {
    if (!user?.migration_code) return;

    try {
      await navigator.clipboard.writeText(user.migration_code);
      setCopySuccess(true);
      toast.success('Migration code copied to clipboard!');
      setTimeout(() => setCopySuccess(false), 2000);
    } catch (err) {
      // console.error('Failed to copy migration code:', err);
      toast.error('Failed to copy migration code');
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      handleCopyMigrationCode();
    }
  };

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-[9999]" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black bg-opacity-25" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-2xl transform overflow-hidden rounded-2xl bg-white p-8 text-left align-middle shadow-xl transition-all">
                <div className="mb-6">
                  <Dialog.Title
                    as="h3"
                    className="text-2xl font-bold text-gray-900"
                  >
                    🚨 Action Required – Nexlev Niche Finder Migration
                  </Dialog.Title>
                </div>

                <div className="space-y-4 mb-6">
                  <div className="space-y-3">
                    <p className="text-gray-700 font-medium">
                      Nexlev Niche Finder has moved to our new central Nexlev
                      Dashboard!
                    </p>
                    <p className="text-gray-700">
                      You are currently accessing the legacy dashboard. Your
                      existing account is also marked as legacy. To ensure
                      continued access and enjoy the full Nexlev ecosystem, all
                      users MUST migrate their accounts by the deadline:{' '}
                      <span className="bg-red-100 text-red-800 px-2 py-1 rounded font-medium">
                        {deadline}
                      </span>
                    </p>
                    <p className="text-gray-700">
                      After this date, the legacy dashboard will be permanently
                      shut down.
                    </p>
                  </div>

                  <div className="bg-gray-50 p-4 rounded-lg">
                    <h4 className="font-semibold text-gray-900 mb-3">
                      How to Migrate Your Account
                    </h4>
                    <ol className="space-y-2 text-gray-700">
                      <li className="flex items-start">
                        <span className="bg-blue-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm mr-3 mt-0.5">
                          1
                        </span>
                        <span>
                          Go to the new dashboard:{' '}
                          <a
                            href={newDashboardUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-blue-600 hover:text-blue-800 underline font-medium"
                          >
                            {newDashboardUrl}
                          </a>
                        </span>
                      </li>
                      <li className="flex items-start">
                        <span className="bg-blue-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm mr-3 mt-0.5">
                          2
                        </span>
                        <span>
                          Create your new central Nexlev account (Google login
                          only)
                        </span>
                      </li>
                      <li className="flex items-start">
                        <span className="bg-blue-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm mr-3 mt-0.5">
                          3
                        </span>
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <span>Enter your migration code:</span>
                            <span
                              onClick={handleCopyMigrationCode}
                              onKeyDown={handleKeyDown}
                              className="bg-blue-100 text-blue-800 px-2 py-1 rounded font-mono text-sm cursor-pointer hover:bg-blue-200 transition-colors select-all"
                              title={copySuccess ? 'Copied!' : 'Copy code'}
                              role="button"
                              tabIndex={0}
                              aria-label={
                                copySuccess
                                  ? 'Migration code copied!'
                                  : 'Copy migration code'
                              }
                            >
                              {user?.migration_code}
                            </span>
                          </div>
                        </div>
                      </li>
                      <li className="flex items-start">
                        <span className="bg-blue-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm mr-3 mt-0.5">
                          4
                        </span>
                        <span>
                          Follow the prompts to complete the migration.
                        </span>
                      </li>
                      <li className="flex items-start">
                        <span className="bg-green-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm mr-3 mt-0.5">
                          ✓
                        </span>
                        <span>
                          Enjoy access to the full Nexlev ecosystem! Your Niche
                          Finder access will be migrated and ready to use with
                          your new account!
                        </span>
                      </li>
                    </ol>
                  </div>

                  <div className="bg-gray-50 border border-gray-200 rounded-lg p-6">
                    <h4 className="font-semibold text-gray-900 mb-4 flex items-center">
                      <span className="text-xl mr-2">⚠️</span>
                      Important Information
                    </h4>
                    <div className="space-y-3 text-sm">
                      <div className="flex items-start">
                        <span className="text-green-500 font-bold text-lg mr-3 mt-0.5">
                          🔐
                        </span>
                        <p className="text-gray-700">
                          <strong>New Authentication:</strong> The new Nexlev
                          dashboard uses Google login only. You&apos;ll need a
                          Google account to access the new platform.
                        </p>
                      </div>
                      <div className="flex items-start">
                        <span className="text-blue-500 font-bold text-lg mr-3 mt-0.5">
                          📝
                        </span>
                        <p className="text-gray-700">
                          <strong>Please Note:</strong> After migrating, you
                          will no longer be able to log in with your legacy
                          email and password. The new unified Nexlev UI will be
                          your only access point going forward.
                        </p>
                      </div>
                      <div className="flex items-start">
                        <span className="text-red-500 font-bold text-lg mr-3 mt-0.5">
                          ⚡
                        </span>
                        <p className="text-gray-700">
                          <strong className="text-red-600">Act now</strong> to
                          avoid any disruption to your Nexlev tools and data!
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="flex justify-center">
                  <button
                    type="button"
                    className="inline-flex justify-center rounded-md border border-transparent bg-blue-600 px-6 py-3 text-base font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                    onClick={handleNewDashboard}
                  >
                    Go to New Dashboard
                  </button>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};

MigrationDialog.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
};

export default MigrationDialog;
